import {
    formatInputDate,
    parseYmd,
    isoWeekStart,
    trailingMean,
} from './revenueMetrics';

/** Forecast horizon (~6 calendar months). */
export const LINEAR_FORECAST_DAYS = Math.round((365 / 12) * 6);

/** Trailing moving-average window on daily gross (chart overlay only). */
export const GROSS_MOVING_AVG_DAYS = 7;

/** Use ~this percentile of gross points to cap the left axis. */
export const MAIN_Y_PERCENTILE = 0.92;

const EWMA_ALPHA = 0.25;
const MIN_HISTORY_DAYS = 28;
const MIN_DAYS_IN_WEEK = 5;
const MIN_WEEKLY_GROSS = 500;
const MAX_WOW_GROWTH_WEEKS = 10;
/** Cap week-on-week change used in projection (±2.5%/week). */
const MAX_WEEKLY_GROWTH_RATE = 0.025;
/** Long-horizon growth fades toward flat run-rate. */
const GROWTH_DECAY_PER_WEEK = 0.96;

const ewmaTerminal = (values, alpha) => {
    if (!values.length) return 0;
    let level = Number(values[0]) || 0;
    for (let i = 1; i < values.length; i += 1) {
        const v = Number(values[i]) || 0;
        level = alpha * v + (1 - alpha) * level;
    }
    return level;
};

const median = (nums) => {
    const sorted = [...nums].filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
    if (!sorted.length) return 0;
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

/** Weekly buckets with day counts — partial weeks skew OLS badly. */
const aggregateWeeklyDetails = (chartData) => {
    const buckets = new Map();
    for (const row of chartData) {
        const weekStart = isoWeekStart(row.date);
        const cur = buckets.get(weekStart) ?? { weekStart, total: 0, dayCount: 0 };
        cur.total += Number(row.grossRevenue) || 0;
        cur.dayCount += 1;
        buckets.set(weekStart, cur);
    }
    return [...buckets.values()].sort((a, b) => a.weekStart.localeCompare(b.weekStart));
};

const usableWeeks = (weeklyDetails) => {
    if (!weeklyDetails.length) return [];
    const lastWeekStart = weeklyDetails[weeklyDetails.length - 1].weekStart;
    return weeklyDetails.filter((w) => {
        if (w.total < MIN_WEEKLY_GROSS || w.dayCount < MIN_DAYS_IN_WEEK) return false;
        if (w.weekStart === lastWeekStart && w.dayCount < 7) return false;
        return true;
    });
};

/** Median week-on-week % change from complete weeks only. */
const estimateWeeklyGrowthRate = (chartData) => {
    const weeks = usableWeeks(aggregateWeeklyDetails(chartData));
    if (weeks.length < 3) return 0;

    const growthRates = [];
    for (let i = 1; i < weeks.length; i += 1) {
        const prev = weeks[i - 1].total;
        const cur = weeks[i].total;
        if (prev >= MIN_WEEKLY_GROSS) {
            growthRates.push((cur - prev) / prev);
        }
    }
    if (!growthRates.length) return 0;

    const recent = growthRates.slice(-MAX_WOW_GROWTH_WEEKS);
    const raw = median(recent);
    return Math.max(-MAX_WEEKLY_GROWTH_RATE, Math.min(MAX_WEEKLY_GROWTH_RATE, raw));
};

/** Compound growth from anchor; growth rate decays so long-range projection stays plausible. */
const projectDailyGross = (level, weeklyGrowthRate, daysAhead) => {
    if (daysAhead <= 0) return level;
    const weeksAhead = daysAhead / 7;
    const effectiveRate =
        Math.abs(weeklyGrowthRate) < 1e-9
            ? 0
            : weeklyGrowthRate * GROWTH_DECAY_PER_WEEK ** Math.max(0, weeksAhead - 2);
    return Math.max(0, level * (1 + effectiveRate) ** weeksAhead);
};

const quantileAscending = (sortedAsc, q) => {
    if (!sortedAsc.length) return 0;
    const pos = (sortedAsc.length - 1) * q;
    const lo = Math.floor(pos);
    const hi = Math.ceil(pos);
    if (lo >= hi) return sortedAsc[lo];
    const t = pos - lo;
    return sortedAsc[lo] + (sortedAsc[hi] - sortedAsc[lo]) * t;
};

const niceCeilEUR = (v) => {
    if (!Number.isFinite(v) || v <= 0) return 500;
    const exp = Math.floor(Math.log10(v));
    const pow = 10 ** exp;
    const n = v / pow;
    const niceBucket = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
    const step = (niceBucket * pow) / 5;
    return Math.ceil(v / step) * step;
};

/**
 * Run-rate anchor + capped median weekly % growth (complete weeks only).
 */
export const buildStableForecast = (chartData) => {
    if (!chartData.length) {
        return {
            level: 0,
            weeklyGrowthRate: 0,
            slopePerWeek: 0,
            hasForecast: false,
        };
    }

    const grossVals = chartData.map((d) => Number(d.grossRevenue) || 0);
    const level = ewmaTerminal(grossVals, EWMA_ALPHA);
    const hasForecast = grossVals.length >= MIN_HISTORY_DAYS;
    const weeklyGrowthRate = hasForecast ? estimateWeeklyGrowthRate(chartData) : 0;
    const slopePerWeek = level * 7 * weeklyGrowthRate;

    return { level, weeklyGrowthRate, slopePerWeek, hasForecast };
};

export const buildComposedChartData = (chartData) => {
    if (!chartData.length) {
        return {
            composedRows: [],
            grossAxisCapped: false,
            mainYMax: undefined,
            marginYMax: undefined,
            forecastMeta: null,
        };
    }

    const grossVals = chartData.map((d) => Number(d.grossRevenue) || 0);
    const grossMa7 = trailingMean(grossVals, GROSS_MOVING_AVG_DAYS);
    const { level, weeklyGrowthRate, slopePerWeek, hasForecast } = buildStableForecast(chartData);
    const n = chartData.length;
    const anchorIdx = n - 1;

    const historical = chartData.map((d, i) => ({
        ...d,
        grossMa7: grossMa7[i],
        trendGross: i === anchorIdx ? level : null,
        isForecast: false,
    }));

    let rolling = parseYmd(chartData[chartData.length - 1].date);
    const forecastRows = [];
    if (hasForecast) {
        for (let k = 0; k < LINEAR_FORECAST_DAYS; k += 1) {
            rolling = new Date(rolling.getFullYear(), rolling.getMonth(), rolling.getDate() + 1);
            const daysAhead = k + 1;
            forecastRows.push({
                date: formatInputDate(rolling),
                cartTotal: null,
                shipping: 0,
                surcharge: 0,
                grossRevenue: null,
                grossMa7: null,
                profit: null,
                orderCount: null,
                trendGross: projectDailyGross(level, weeklyGrowthRate, daysAhead),
                isForecast: true,
            });
        }
    }

    const composedRows = [...historical, ...forecastRows];

    const grossDailySorted = [...grossVals].sort((a, b) => a - b);
    const bulk =
        grossDailySorted.length === 0
            ? 0
            : quantileAscending(grossDailySorted, MAIN_Y_PERCENTILE) || 0;
    const grossHardMax = grossVals.length === 0 ? 0 : Math.max(...grossVals);

    let trendPeak = level;
    let maPeak = 0;
    for (const r of composedRows) {
        if (r.grossMa7 != null) maPeak = Math.max(maPeak, Number(r.grossMa7) || 0);
        if (r.isForecast && r.trendGross != null) {
            trendPeak = Math.max(trendPeak, Number(r.trendGross) || 0);
        }
    }

    let mainYMax = Math.max(800, bulk * 1.14, maPeak * 1.06, trendPeak * 1.08);
    mainYMax = niceCeilEUR(mainYMax);

    let grossAxisCapped = false;
    if (grossHardMax > 0) {
        if (mainYMax >= grossHardMax * 0.98) {
            mainYMax = niceCeilEUR(grossHardMax * 1.08);
        } else {
            grossAxisCapped = grossHardMax > mainYMax * 1.02;
        }
    }

    let marginPeak = 0;
    for (const d of chartData) {
        const m = (Number(d.shipping) || 0) + (Number(d.surcharge) || 0);
        marginPeak = Math.max(marginPeak, m);
    }
    const marginYMax = niceCeilEUR(Math.max(80, marginPeak * 1.25));

    const projected6mo = hasForecast
        ? projectDailyGross(level, weeklyGrowthRate, LINEAR_FORECAST_DAYS)
        : null;

    const growthPctDisplay = weeklyGrowthRate * 100;

    return {
        composedRows,
        grossAxisCapped,
        mainYMax,
        marginYMax,
        forecastMeta: {
            hasForecast,
            level,
            slopePerWeek,
            weeklyGrowthRate,
            growthPctDisplay,
            projected6mo,
        },
    };
};

export const getForecastBand = (composedRows) => {
    const rows = composedRows.filter((r) => r.isForecast);
    if (!rows.length) return null;
    return { x1: rows[0].date, x2: rows[rows.length - 1].date };
};
